import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaTimes, FaSortUp, FaSortDown, FaDownload } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import DateRangeFilter from '../components/DateRangeFilter';
import { fetchBookDownloadsList, exportBookDownloadsCsv } from '../services/api';
import '../components/DataTable.css';
import './DataPages.css';
import './Pages.css';

const toUTCDateStr = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - 29);
  return {
    startDate: toUTCDateStr(start),
    endDate: toUTCDateStr(end)
  };
};

const BookDownloads = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('DESC');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [startDate, setStartDate] = useState(getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(getDefaultDateRange().endDate);
  const [exporting, setExporting] = useState(false);
  const pageSize = 100;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchBookDownloadsList(page, pageSize, sortOrder, searchTerm, startDate, endDate);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, sortOrder, searchTerm, startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleSortToggle = () => {
    setSortOrder(prev => prev === 'DESC' ? 'ASC' : 'DESC');
    setPage(1);
  };

  const handleDateApply = () => {
    setPage(1);
  };

  const handleDownloadCsv = async () => {
    try {
      setExporting(true);
      const response = await exportBookDownloadsCsv(sortOrder, searchTerm, startDate, endDate);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}h${String(now.getMinutes()).padStart(2,'0')}m${String(now.getSeconds()).padStart(2,'0')}s`;
      link.download = `Book_Downloads_${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const items = data ? data.items : [];

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Book Downloads</h1>
        </div>
      </div>

      <div className="data-page-content">
        {error && <div className="table-empty">Error: {error}</div>}

        {!error && (
          <>
            <div className="filters-container">
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                onApply={handleDateApply}
                earliestDate={data?.earliestDate}
                latestDate={data?.latestDate}
              />
            </div>

            <div className="data-page-controls">
              <div className="table-search-wrapper">
                <FaSearch className="table-search-icon" size={14} />
                <input
                  type="text"
                  className="table-search-input"
                  placeholder="Search by ID, title, or language..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                {searchInput && (
                  <button className="table-search-clear" onClick={handleSearchClear}>
                    <FaTimes size={14} />
                  </button>
                )}
                <button className="table-search-btn" onClick={handleSearch} title="Search">
                  <FaSearch size={14} />
                </button>
              </div>

              <button className="btn-primary" onClick={handleDownloadCsv} title="Download CSV" disabled={exporting}>
                <FaDownload size={14} />
                <span>{exporting ? 'Exporting ...' : 'CSV'}</span>
              </button>

              {data && (
                <div className="control-badge" style={{ marginLeft: 'auto' }}>
                  <span className="badge-label">Viewing</span>
                  <span className="badge-value">{data.totalCount.toLocaleString()}</span>
                </div>
              )}
            </div>

            {loading && <div className="table-loading">Loading ...</div>}

            {!loading && items.length > 0 && (
              <div className="data-page-table-card">
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Book ID</th>
                        <th>Book Title</th>
                        <th>Language</th>
                        <th>Published</th>
                        <th className="sortable" onClick={handleSortToggle}>
                          Download Count
                          <span className="sort-indicator">
                            {sortOrder === 'ASC' ? <FaSortUp size={12} /> : <FaSortDown size={12} />}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={`${item.bookId}-${item.language}-${index}`}>
                          <td>{item.bookId}</td>
                          <td>{item.bookTitle || '-'}</td>
                          <td>{item.language || '-'}</td>
                          <td>{item.isPublished ? 'Yes' : 'No'}</td>
                          <td>{item.downloadCount.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  page={data.page}
                  totalPages={data.totalPages}
                  totalCount={data.totalCount}
                  pageSize={data.pageSize}
                  onPageChange={setPage}
                />
              </div>
            )}

            {!loading && data && items.length === 0 && (
              <div className="table-empty">
                {searchTerm || startDate || endDate
                  ? 'No results found for the current filters.'
                  : 'No book download data found.'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookDownloads;
